import { useEffect, useState } from "react";

import type { RiskPictureItem } from "./risk-picture-model";

export const POLYGON_FADE_MS = 150;

export interface FadingPolygon {
  item: RiskPictureItem;
  /** 0 or 1. Deck.gl interpolates between them; this is not the in-between value. */
  opacity: number;
  /** Set once the polygon is no longer in the day's visible items. */
  leaving?: boolean;
}

/** Keep shown polygons, fade out the ones that left, and start new ones at 0. */
export const fadeRiskPicturePolygons = (
  current: FadingPolygon[],
  next: RiskPictureItem[],
): FadingPolygon[] => {
  const nextById = new Map(next.map((item) => [item.id, item]));
  const seen = new Set<string>();
  const result: FadingPolygon[] = [];

  for (const entry of current) {
    const item = nextById.get(entry.item.id);
    if (item) {
      seen.add(item.id);
      result.push({ item, opacity: 1 });
      continue;
    }
    // Already finished leaving; the drop step removes it.
    if (entry.leaving && entry.opacity === 0) continue;
    result.push({ item: entry.item, opacity: 0, leaving: true });
  }

  for (const item of next) {
    if (!seen.has(item.id)) result.push({ item, opacity: 0 });
  }

  return result;
};

/** Entering polygons (opacity 0, not leaving) become visible on the next frame. */
export const revealEnteringPolygons = (current: FadingPolygon[]): FadingPolygon[] => {
  if (!current.some((entry) => entry.opacity === 0 && !entry.leaving)) return current;
  return current.map((entry) =>
    entry.opacity === 0 && !entry.leaving ? { ...entry, opacity: 1 } : entry,
  );
};

/** Polygons whose fade-out has been given time to finish. */
export const dropLeftPolygons = (current: FadingPolygon[]): FadingPolygon[] => {
  if (!current.some((entry) => entry.leaving && entry.opacity === 0)) return current;
  return current.filter((entry) => !(entry.leaving && entry.opacity === 0));
};

const easeOut = (t: number): number => t * (2 - t);

/**
 * Thumbnails pass no opacity. A polygon that just (re)entered is at 0 and not
 * leaving: transitions stay off, because deck.gl would otherwise fade it from
 * its default opacity of 1 and the fade-in would not play. Leaving and shown
 * polygons keep the transition, so a return from 0 to 1 fades as well.
 */
export const polygonOpacityTransition = (
  opacity: number | undefined,
  leaving: boolean | undefined,
): { duration: number; easing: (t: number) => number } | undefined => {
  if (opacity === undefined || (opacity === 0 && !leaving)) return undefined;
  return { duration: POLYGON_FADE_MS, easing: easeOut };
};

const shown = (items: RiskPictureItem[]): FadingPolygon[] =>
  items.map((item) => ({ item, opacity: 1 }));

/**
 * Big-map polygons only. A polygon stays mounted while it fades out, and a new
 * one mounts at opacity 0 so deck.gl can fade it in. The first set does not fade.
 */
export const useFadingRiskPicturePolygons = (items: RiskPictureItem[]): FadingPolygon[] => {
  const [polygons, setPolygons] = useState<FadingPolygon[]>(() => shown(items));

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPolygons(shown(items));
      return;
    }

    setPolygons((current) => fadeRiskPicturePolygons(current, items));
    const frame = requestAnimationFrame(() => {
      setPolygons(revealEnteringPolygons);
    });
    const timer = window.setTimeout(() => {
      setPolygons(dropLeftPolygons);
    }, POLYGON_FADE_MS);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [items]);

  return polygons;
};
